import startCase from 'lodash/startCase';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import moment from 'moment-timezone';
import {ActionType} from 'redux-promise-middleware';
import {document} from 'ssr-window';
import StorageUtils from '@spothero/utils/storage';
import SearchUtils from 'search/search-utils';
import GoogleRemarketingUtils from 'utils/google-remarketing';
import GTMUtils from 'utils/gtm';
import SearchTracking from 'utils/search-tracking';
import SegmentUtils from 'utils/segment';
import SpotUtils from 'utils/spot';
import UserUtils from 'user/user-utils';
import {USER_LOGIN, USER_LOGOUT, USER_UPDATE} from 'user/user-actions';
import {CHECKOUT_FETCH_RATES} from 'checkout/checkout-actions';
import {SEARCH_REQUEST_SET_TERM} from 'search-request/search-request-actions';
import {SPOT_SET_TERM} from 'spot/spot-actions';
import {
    SPOTS_RADIAL_INCREMENT,
    SPOTS_TILED_INCREMENT,
    SPOTS_GET_MOBILE,
    SPOTS_GET_MOBILE_PAGINATION,
    SPOTS_GET_RADIAL,
    SPOTS_GET_AIRPORT,
} from 'spots/spots-actions';
import {FILTERS_RESET, FILTERS_UPDATE} from 'filters/filters-actions';
import trackProductListFiltered from 'segment/events/product-list-filtered';

const ANALYTICS_SEARCH_UUID_COOKIE = 'sh-analytics-search-uuid';
const TTFR_SESSION = 'sh-ttfr-tracked';

export const analyticsMiddleware = store => next => action => {
    const {type, payload, meta} = action;

    switch (type) {
        case `${USER_LOGIN}_${ActionType.Fulfilled}`:
        case USER_UPDATE: {
            const storedUser = store.getState().user.data;
            const newUser = payload.id && storedUser.id !== payload.id;

            if (newUser) {
                UserUtils.identify(payload);
            }

            break;
        }

        case `${USER_LOGOUT}_${ActionType.Fulfilled}`: {
            StorageUtils.remove('sh-session-uuid', 'cookie');
            UserUtils.identify(null);

            break;
        }

        case `${SPOTS_GET_RADIAL}_${ActionType.Pending}`:
        case `${SPOTS_GET_MOBILE}_${ActionType.Pending}`: {
            if (!StorageUtils.get(TTFR_SESSION, 'session')) {
                SegmentUtils.startTimeToFirstResultTimer();
            }

            break;
        }

        case SPOTS_RADIAL_INCREMENT:
        case SPOTS_TILED_INCREMENT:
        case `${SPOTS_GET_AIRPORT}_${ActionType.Fulfilled}`:
        case `${SPOTS_GET_MOBILE}_${ActionType.Fulfilled}`:
        case `${SPOTS_GET_MOBILE_PAGINATION}_${ActionType.Fulfilled}`: {
            const {
                city: {
                    data: {display_name: cityName, slug: citySlug, state},
                },
                destination: {
                    data: {
                        id: destinationId,
                        title: destinationName,
                        google_places: destinationGooglePlaces,
                        airport,
                    },
                },
                event: {
                    data: {id: eventId, title: eventName},
                },
                search: {
                    data: {currentIncrement, destinationTitle},
                },
                searchRequest: {
                    monthly,
                    referrer,
                    search_string: searchQuery,
                    latitude,
                    longitude,
                    starts,
                    ends,
                },
                filters: {activeAmenities},
            } = store.getState();

            if (
                !StorageUtils.get(TTFR_SESSION, 'session') &&
                (type === `${SPOTS_GET_MOBILE}_${ActionType.Fulfilled}` ||
                    (type === SPOTS_RADIAL_INCREMENT && currentIncrement === 1))
            ) {
                SegmentUtils.trackTimeToFirstResult();
            }

            if (
                currentIncrement === 1 ||
                (meta && meta.isMobileRequest) ||
                type === `${SPOTS_GET_AIRPORT}_${ActionType.Fulfilled}`
            ) {
                const analyticsSearchUUIDCookie = StorageUtils.get(
                    ANALYTICS_SEARCH_UUID_COOKIE,
                    'cookie'
                );
                const userLatLng = StorageUtils.get(
                    'sh-user-location',
                    'session'
                );
                const {
                    sessionUUID,
                    searchUUID,
                    action: trackingAction,
                } = SearchTracking.getValues();
                const parkingType = airport
                    ? 'airport'
                    : monthly
                    ? 'monthly'
                    : 'transient';
                const searchSource = !isEmpty(referrer) ? referrer : 'map';
                const googlePlaceId = get(
                    destinationGooglePlaces,
                    '0.google_places_place_id'
                );
                const googlePlaceName = get(
                    destinationGooglePlaces,
                    '0.google_places_name'
                );
                /* eslint-disable camelcase */
                // Properties that are MARKED FOR DEPRECATION will no longer appear in Segment Protocols definitions.
                // These will not be removed from our application code until a deprecation plan has been
                // communicated to downstream users of this data.
                const searchEventProps = {
                    'search query': searchQuery || destinationTitle, // MARKED FOR DEPRECATION: query will be used moving forward
                    query: searchQuery || destinationTitle,
                    'search city': cityName, // MARKED FOR DEPRECATION: city will be used moving forward
                    city: citySlug,
                    state,
                    'session id': sessionUUID, // MARKED FOR DEPRECATION: session_id will be used moving forward
                    session_id: sessionUUID,
                    'search id': searchUUID, // MARKED FOR DEPRECATION: search_id will be used moving forward
                    search_id: searchUUID,
                    'parking type': parkingType, // MARKED FOR DEPRECATION: parking_type will be used moving forward
                    parking_type: parkingType,
                    'search type': trackingAction, // MARKED FOR DEPRECATION: action_type will be used moving forward
                    action_type: trackingAction,
                    'search source': searchSource, // MARKED FOR DEPRECATION: search_source will be used moving forward
                    search_source: searchSource,
                    'number of filters applied': activeAmenities.length,
                    'filters selected': activeAmenities.join(),
                    'search start time': moment(starts).toISOString(), // MARKED FOR DEPRECATION: search_start_time_utc will be used moving forward
                    search_start_time_utc: moment(starts).toISOString(),
                    'search end time': monthly
                        ? null
                        : moment(ends).toISOString(), // MARKED FOR DEPRECATION: search_end_time_utc will be used moving forward
                    search_end_time_utc: monthly
                        ? null
                        : moment(ends).toISOString(),
                    map_center: {
                        latitude,
                        longitude,
                    },
                    destination_id: destinationId,
                    destination_name: destinationName,
                    event_id: eventId,
                    event_name: eventName,
                    google_places_name: googlePlaceName,
                    google_places_place_id: googlePlaceId,
                    ...(userLatLng && {
                        'user location': userLatLng, // MARKED FOR DEPRECATION: user_location will be used moving forward
                        user_location: userLatLng,
                    }),
                    ...(airport && {
                        'airport code search': airport.iata_code, // MARKED FOR DEPRECATION: airport_code will be used moving forward
                        airport_code: airport.iata_code,
                        top_search_results_list: payload.results.map(
                            ({facility: {id, tag}}) => ({
                                facility_id: id,
                                tag,
                            })
                        ),
                    }),
                };
                /* eslint-enable camelcase */

                if (analyticsSearchUUIDCookie !== searchUUID) {
                    SegmentUtils.track({
                        event: 'Products Searched',
                        properties: searchEventProps,
                        additionalOptions: {
                            context: {
                                protocols: {
                                    // eslint-disable-next-line camelcase
                                    event_version: 3,
                                },
                            },
                        },
                    });

                    StorageUtils.set(
                        ANALYTICS_SEARCH_UUID_COOKIE,
                        searchUUID,
                        'cookie'
                    );
                }
            }

            break;
        }

        case SEARCH_REQUEST_SET_TERM: {
            const {airport, monthly} = payload;
            const termString = airport
                ? 'airport'
                : monthly
                ? 'monthly'
                : 'transient';

            GoogleRemarketingUtils.track({
                data: {
                    /* eslint-disable camelcase */
                    dynx_itemid: 's_NA',
                    dynx_itemid2: termString,
                    dynx_pagetype: 'searchresults',
                    /* eslint-enable camelcase */
                },
            });

            break;
        }

        case SPOT_SET_TERM: {
            const {
                payload: {newRate, monthly},
            } = action;

            const googleRemarketingData = {
                /* eslint-disable camelcase */
                dynx_itemid: `f_${
                    store.getState().spot.selected.parking_spot_id
                }`,
                dynx_itemid2: monthly ? 'monthly' : 'transient',
                dynx_pagetype: 'conversionintent',
                /* eslint-enable camelcase */
            };

            if (newRate) {
                // eslint-disable-next-line camelcase
                googleRemarketingData.dynx_totalvalue = newRate.price / 100;
            }

            GoogleRemarketingUtils.track({
                data: googleRemarketingData,
            });

            GTMUtils.push({
                event: 'priceUpdated',
                rentalPrice: newRate ? (newRate.price / 100).toString() : 'N/A',
                currencyType: newRate
                    ? newRate.currency_type
                    : UserUtils.CURRENCY_TYPES.USD.toLowerCase(),
            });

            break;
        }

        case `${SPOTS_GET_RADIAL}_${ActionType.Fulfilled}`: {
            const {
                search: {
                    data: {searchType},
                },
            } = store.getState();

            SegmentUtils.page({
                name: `${startCase(searchType)} Search`,
            });

            break;
        }

        case `${CHECKOUT_FETCH_RATES}_${ActionType.Fulfilled}`: {
            const {
                payload: {
                    data: {data},
                },
                meta: {monthly, spotId},
            } = action;
            const {
                checkout: {previousRental},
                city: {
                    data: {timezone},
                },
                searchRequest: {starts, ends, airport, referrer, pageType},
                spot: {selected: selectedSpot},
                search: {
                    data: {referrerAffiliate},
                },
            } = store.getState();
            const isAirportSpotDetail = pageType === 'facility' && airport;
            const newRate = SpotUtils.prepare({
                spot: {
                    ...selectedSpot,
                    ...data,
                },
                searchRequest: {
                    monthly,
                },
                timezone,
            }).selectedRate;
            const newPrice = newRate ? (newRate.price / 100).toString() : 'N/A';
            const newCurrencyType = newRate
                ? newRate.currency_type
                : UserUtils.CURRENCY_TYPES.USD.toLowerCase();
            const startsMoment =
                newRate && newRate.starts
                    ? moment(newRate.starts)
                    : moment(starts);
            const endsMoment =
                newRate && newRate.ends
                    ? moment(newRate.ends)
                    : monthly
                    ? startsMoment.clone().add(1, 'month')
                    : moment(ends);
            const id = selectedSpot ? selectedSpot.spotId : spotId;
            const rentalType = airport
                ? 'airport'
                : monthly
                ? 'monthly'
                : 'transient';
            const googleRemarketingTrackingData = {
                /* eslint-disable camelcase */
                dynx_itemid: `f_${id}`,
                dynx_itemid2: rentalType,
                dynx_pagetype: 'conversionintent',
                /* eslint-enable camelcase */
            };
            const fromScreen = SearchUtils.getReferrerDetails({
                referrer,
                previousRental,
            });

            if (newRate) {
                // eslint-disable-next-line camelcase
                googleRemarketingTrackingData.dynx_totalvalue =
                    newRate.price / 100;
            }

            if (!isAirportSpotDetail) {
                GoogleRemarketingUtils.track({
                    data: googleRemarketingTrackingData,
                });

                SegmentUtils.page({
                    name: 'Checkout',
                    properties: {
                        currency: newCurrencyType,
                        price: newPrice,
                        'spot id': id,
                        'reservation duration': parseInt(
                            moment
                                .duration(endsMoment.diff(startsMoment))
                                .format('m'),
                            10
                        ),
                        'time until reservation start': parseInt(
                            moment
                                .duration(startsMoment.diff(moment()))
                                .format('m'),
                            10
                        ),
                        'from screen': fromScreen,
                        'parking type': rentalType,
                        referrer: referrer || document.referrer,
                        'referrer affiliate': referrerAffiliate,
                    },
                });
            }

            GTMUtils.push({
                event: 'priceUpdated',
                rentalPrice: newPrice,
                currencyType: newCurrencyType,
                spotId: id,
                rentalType,
            });

            break;
        }

        case FILTERS_RESET:
        case FILTERS_UPDATE: {
            const {
                city: {
                    data: {slug: citySlug},
                },
                filters,
                search: {sortBy},
                searchRequest: {monthly: isMonthly, pageType},
                spots: {data: spots},
            } = store.getState();

            trackProductListFiltered({
                citySlug,
                pageType,
                isMonthly,
                spots,
                sortBy,
                filters: {
                    ...filters,
                    ...payload,
                    ...(type === FILTERS_RESET && {activeAmenities: []}),
                },
            });

            break;
        }
    }

    return next(action);
};
